import type { Customer } from '@clockwise/clients/customers';
import { CheckSlot, Combobox } from '@/components/custom/combobox';
import { customerPickerConfig } from '@/components/custom/entity-pickers';
import { PickerTruncationNotice } from '@/components/custom/picker-truncation-notice';
import {
  type LoadStrategy,
  useEntityAutocomplete,
} from '@/components/custom/use-entity-autocomplete';

// Picker for a customer. See `useEntityAutocomplete` for `loadStrategy`
// semantics. Defaults to 'lazy' (server-side `q=` search per keystroke)
// so the picker scales to tenants with thousands of customers without
// shipping the whole list to the browser. Pass `loadStrategy="preload"`
// explicitly when you know the tenant's catalog is small AND you want
// client-side filtering — but the existing callers that need that path
// already pass `items={...}` (a pre-resolved team-scoped list) which
// bypasses the internal fetch entirely, so 'preload' is rarely the
// right choice on this picker today. If lookups become the perf
// bottleneck, an in-memory (e.g. Redis) cache of `{tenantId →
// active customers}` is the planned follow-up.
interface Props {
  value: string | null;
  onChange: (customer: Customer | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Forwarded to the trigger so a `<Label htmlFor>` can point at it. */
  id?: string;
  loadStrategy?: LoadStrategy;
  /** Page size for the one-shot 'preload' fetch (in-memory filtering). Only
   *  used when `loadStrategy="preload"`; capped at the server's 500-row limit.
   *  Defaults to the hook's 200. */
  preloadLimit?: number;
  /** Open the popover on mount. Useful for inline-edit cells that activate
   *  the picker on demand and want the dropdown to appear immediately. */
  defaultOpen?: boolean;
  /** Forward popover open/close to the parent — lets a singleton-mount
   *  caller know when the user dismissed without selecting. */
  onOpenChange?: (open: boolean) => void;
  /** Hide the inline clear affordance. Use for contexts where "no customer"
   *  is not a valid state (e.g. an existing timesheet row whose customer
   *  can't be nulled — only swapped). Defaults to true. */
  clearable?: boolean;
  /** Pre-resolved customer list. When provided, the internal `loadStrategy`
   *  fetch is bypassed entirely — used by the timesheet flow to scope the
   *  dropdown to customers the target user is on the team of for the week.
   *  The caller is responsible for the cache lifecycle. */
  items?: readonly Customer[];
  /** Pre-resolved selected customer. Paired with `items` when the picker is
   *  fed externally — without it, a selected value that isn't in `items` would
   *  fall back to the customer's id literal.
   *
   *  In the normal (fetching) mode this acts as a FALLBACK label for `value`,
   *  used when the picker's own `GET /api/customers/:id` can't resolve it.
   *  That is not hypothetical: `customer.view: assigned` 403s on a customer the
   *  caller isn't assigned to, so a subsidiary's parent picker would otherwise
   *  render blank — and saving the form would then write that blank back and
   *  silently detach the parent. Callers that can name the current value (the
   *  customer form, from `parentName`) should always pass it.
   *
   *  It is only ever used when its `id` equals the current `value`, so a caller
   *  may keep passing the same object as the selection changes — it simply
   *  stops applying once it no longer describes what is selected. */
  selectedItem?: Customer | null;
}

// List/detail fetch + key contract is shared with EntityMultiSelect via
// `entity-pickers` so the two never compute divergent query keys.
const {
  getId: getCustomerId,
  getLabel: customerLabel,
  fetchList: fetchCustomerList,
  fetchOne: fetchCustomerOne,
  listKey: customerListKey,
  detailKey: customerDetailKey,
  lazyLimit: customerLazyLimit,
  maxLimit: customerMaxLimit,
} = customerPickerConfig;
const customerFilter = (c: Customer, q: string) => {
  const hay = `${c.name} ${c.qbId ?? ''}`.toLowerCase();
  return hay.includes(q.toLowerCase());
};

export function CustomerAutocomplete({
  value,
  onChange,
  disabled,
  placeholder = 'Select customer…',
  className,
  loadStrategy = 'lazy',
  preloadLimit,
  defaultOpen = false,
  onOpenChange,
  clearable = true,
  items,
  selectedItem,
  id,
}: Props) {
  const externallyProvided = items !== undefined;
  // When the caller passes a pre-resolved list, skip the internal fetch
  // entirely. The hook still drives the open + query callbacks so the
  // Combobox state machine stays correct; we just ignore its `items`
  // and `selectedItem` outputs in favor of the props.
  const auto = useEntityAutocomplete<Customer>({
    value,
    strategy: externallyProvided ? 'preload' : loadStrategy,
    fetchList: externallyProvided ? () => Promise.resolve({ data: [] }) : fetchCustomerList,
    fetchOne: fetchCustomerOne,
    listKey: customerListKey,
    detailKey: customerDetailKey,
    lazyLimit: customerLazyLimit,
    maxLimit: customerMaxLimit,
    preloadLimit,
  });

  const resolvedItems = externallyProvided ? items : auto.items;
  // The fallback describes ONE specific customer, so it may only stand in for
  // that customer. Without the id check it outlives the value it describes:
  // `Combobox` resolves `selectedItem ?? …` and never consults `value`, so a
  // cleared picker (value → null, detail query disabled) would keep rendering
  // the old label — writing it back over the input the user just emptied — and
  // a switched parent would show the previous customer's name until, or
  // unless, the new one's fetch resolves.
  const fallbackMatchesValue = selectedItem != null && selectedItem.id === value;
  const resolvedSelected = externallyProvided
    ? (selectedItem ?? (items ?? []).find((c) => c.id === value) ?? null)
    : // The fetched row is authoritative once it arrives; the caller's copy
      // only fills the gap while it can't be fetched at all (see prop docs).
      (auto.selectedItem ?? (fallbackMatchesValue ? selectedItem : null));
  // In external-items mode the dropdown is always client-side; the
  // Combobox filters in memory and we never call onQueryChange.
  const useClientFilter = externallyProvided || auto.isClient;

  const handleOpenChange = (open: boolean) => {
    auto.setOpen(open);
    onOpenChange?.(open);
  };

  return (
    <Combobox<Customer>
      items={resolvedItems}
      selectedItem={resolvedSelected}
      value={value}
      onChange={onChange}
      getId={getCustomerId}
      getLabel={customerLabel}
      filterFn={useClientFilter ? customerFilter : undefined}
      onQueryChange={useClientFilter ? undefined : auto.setQuery}
      onOpenChange={handleOpenChange}
      loading={externallyProvided ? false : auto.loading}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      id={id}
      defaultOpen={defaultOpen}
      clearable={clearable}
      // Customer names can be longer than the picker's trigger (e.g. a
      // narrow timesheet cell), so let the popover grow past the anchor
      // width up to the Combobox's auto cap. Matches the product picker.
      popoverWidth="auto"
      // The catalog outgrew one page: say so rather than letting the visible
      // rows pass for every customer. Never fires in externally-fed mode —
      // that list is the caller's, and the stub fetch returns no `meta`.
      renderFooter={
        auto.truncated
          ? () => (
              <PickerTruncationNotice
                truncated={auto.truncated}
                noun="customers"
                clientFiltered={useClientFilter}
              />
            )
          : undefined
      }
      renderItem={(c, { isSelected }) => (
        <div className="flex min-w-0 items-center gap-1.5">
          <CheckSlot isSelected={isSelected} />
          <span className="truncate font-medium">{c.name}</span>
        </div>
      )}
    />
  );
}
